import React, { useState } from "react";
import {
  ShieldCheck,
  Gauge,
  Timer,
  Loader2,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import { toast } from "react-toastify";
import useSWR from "swr";
import {
  saveSecuritySettings,
  type SecuritySettings as SecuritySettingsData,
} from "../../api/securitySettingsApis";
import logger from "../../utils/logger";

type ToggleKey = "rateLimitEnabled" | "otpCooldownEnabled";

const TOGGLES: {
  key: ToggleKey;
  label: string;
  description: string;
  icon: LucideIcon;
}[] = [
  {
    key: "rateLimitEnabled",
    label: "API Rate Limiting",
    description:
      "Limits requests per client (per-minute window and per-second burst). When off, no request is rejected with 429.",
    icon: Gauge,
  },
  {
    key: "otpCooldownEnabled",
    label: "OTP Cooldown",
    description:
      "Wait time between OTP resends, the send-count limit and progressive blocking after repeated requests. When off, OTPs can be resent immediately.",
    icon: Timer,
  },
];

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const SecuritySettings: React.FC = () => {
  const [savingKey, setSavingKey] = useState<ToggleKey | null>(null);

  const { data, mutate, isLoading } = useSWR<{
    data: SecuritySettingsData;
  }>("/api/admin/security-settings");

  const settings = data?.data;
  const anyDisabled =
    settings && (!settings.rateLimitEnabled || !settings.otpCooldownEnabled);

  const onToggle = async (key: ToggleKey) => {
    if (!settings) return;
    const nextValue = !settings[key];
    setSavingKey(key);
    try {
      const res = await saveSecuritySettings({ [key]: nextValue });
      const resData = res?.data;
      if (resData?.status) {
        toast.success(resData?.message ?? "Settings updated");
        mutate({ data: resData.data }, { revalidate: false });
      } else {
        toast.error(resData?.message ?? "Failed to update settings");
      }
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Failed to update settings"));
      logger.log("Error saving security settings:", error);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="min-h-screen max-w-screen w-full ">
      <div className="w-full max-w-full lg:p-4 md:p-4 p-4">
        {/* Header */}
        <div className="mb-4 flex items-center space-x-3">
          <div className="bg-gray-100 rounded-full p-3">
            <ShieldCheck className="h-6 w-6 text-gray-700" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Security Settings
          </h1>
        </div>

        {/* Info notice */}
        <div
          className={`mb-4 flex items-start gap-3 rounded-xl border p-4 text-sm ${
            anyDisabled
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-amber-200 bg-amber-50 text-amber-900"
          }`}
        >
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            {anyDisabled
              ? "One or more protections are off. The platform is more exposed to brute-force and OTP/SMS abuse until they are turned back on."
              : "Changes apply to all backend instances within about 15 seconds, without a restart."}
          </p>
        </div>

        {/* Toggles */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
          {isLoading || !settings ? (
            <div className="p-8 flex justify-center text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            TOGGLES.map((item) => {
              const enabled = settings[item.key];
              const Icon = item.icon;
              const saving = savingKey === item.key;
              return (
                <div
                  key={item.key}
                  className="p-4 flex items-center justify-between gap-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="bg-gray-100 rounded-lg p-2 mt-0.5">
                      <Icon className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-800">
                          {item.label}
                        </h3>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            enabled
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {enabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`Toggle ${item.label}`}
                    disabled={savingKey !== null}
                    onClick={() => onToggle(item.key)}
                    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                      enabled ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow transition-transform ${
                        enabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    >
                      {saving && (
                        <Loader2 className="h-3 w-3 animate-spin text-gray-500" />
                      )}
                    </span>
                  </button>
                </div>
              );
            })
          )}
        </div>

        {settings?.updatedAt && (
          <p className="text-xs text-gray-400 mt-3">
            Last updated {new Date(settings.updatedAt).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default SecuritySettings;
